import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminLayout";
import api from "@/lib/api";
import { Badge, qualClass } from "@/components/StatusBadge";
import { QUALIFICATION_OPTIONS, PROGRESS_OPTIONS } from "@/data/intakeConfig";
import { toast } from "sonner";
import { EnrollPanel } from "@/components/portal/EnrollPanel";
import { TeamAssign } from "@/components/portal/TeamAssign";
import {
  ArrowLeft, User, FileText, GraduationCap, CalendarCheck, StickyNote, Mail,
  Send, FolderOpen, History, Save, Upload, Truck, Headset, Plus, Download, Lock, Check,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: User },
  { key: "intake", label: "Original Intake", icon: FileText },
  { key: "qualification", label: "Qualification", icon: GraduationCap },
  { key: "interview", label: "Interview", icon: CalendarCheck },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "communications", label: "Communications", icon: Mail },
  { key: "referrals", label: "Referrals", icon: Send },
  { key: "documents", label: "Documents", icon: FolderOpen },
  { key: "activity", label: "Activity History", icon: History },
];

const Field = ({ label, value }) => (
  <div><p className="text-xs text-dark-text/50 uppercase tracking-wide">{label}</p><p className="text-sm text-navy font-medium mt-0.5">{value || "—"}</p></div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-lg border border-navy/10 shadow-sm p-5">
    <h3 className="font-display font-bold text-navy mb-4">{title}</h3>{children}
  </div>
);

const renderObj = (obj) => Object.entries(obj || {}).map(([k, v]) => (
  <Field key={k} label={k.replace(/_/g, " ")} value={v === null || v === undefined || v === "" ? "—" : (typeof v === "boolean" ? (v ? "Yes" : "No") : String(v))} />
));

export default function ApplicantProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    api.get(`/admin/applicants/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Failed to load applicant"));
  }, [id]);
  useEffect(load, [load]);

  if (!d) return <AdminLayout title="Applicant"><p className="text-dark-text/50">Loading…</p></AdminLayout>;

  const g = d.general || {};
  const name = `${g.first_name || ""} ${g.last_name || ""}`.trim();

  return (
    <AdminLayout title="Applicant Profile">
      <button onClick={() => navigate("/admin/applicants")} className="inline-flex items-center gap-2 text-navy font-semibold text-sm mb-4 hover:text-magenta">
        <ArrowLeft size={16} /> Back to Applicants
      </button>

      <div className="bg-white rounded-lg border border-navy/10 shadow-sm p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-black text-navy">{name}</h2>
            <p className="text-sm text-dark-text/60 font-mono">{d.reference_number}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {d.programs.map((p) => <span key={p} className="bg-navy-light text-navy text-xs font-semibold px-3 py-1 rounded-full">{p}</span>)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={qualClass(d.qualification)}>{d.qualification}</Badge>
            <span className="text-xs text-dark-text/60">{d.progress_status}</span>
            {typeof (d.cdl_result || {}).score === "number" && <span className="text-sm font-bold text-magenta">CDL Score: {d.cdl_result.score}/100</span>}
          </div>
        </div>
      </div>

      <EnrollPanel applicantId={d.applicant_id} reload={load} />

      <div className="flex gap-1 overflow-x-auto border-b border-navy/10 mb-5" data-testid="profile-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-magenta text-magenta" : "border-transparent text-dark-text/60 hover:text-navy"}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      <div data-testid={`panel-${tab}`}>
        {tab === "overview" && <Overview d={d} g={g} />}
        {tab === "intake" && <IntakeTab d={d} g={g} />}
        {tab === "qualification" && <QualificationTab d={d} reload={load} />}
        {tab === "interview" && <InterviewTab d={d} reload={load} />}
        {tab === "notes" && <NotesTab d={d} reload={load} />}
        {tab === "communications" && <CommsTab d={d} reload={load} />}
        {tab === "referrals" && <ReferralsTab d={d} reload={load} />}
        {tab === "documents" && <DocsTab d={d} reload={load} />}
        {tab === "activity" && <ActivityTab d={d} />}
      </div>
    </AdminLayout>
  );
}

function Overview({ d, g }) {
  const a = d.assignments || {};
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Section title="Contact Information">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email" value={g.email} /><Field label="Phone" value={g.phone} />
          <Field label="Address" value={`${g.street || ""} ${g.city || ""} ${g.state || ""} ${g.zip || ""}`} />
          <Field label="Preferred Contact" value={g.preferred_contact} />
          <Field label="AAWTA Member" value={g.aawta_member} /><Field label="Member Number" value={g.member_number} />
          <Field label="Date of Birth" value={g.dob} /><Field label="Age" value={g.age} />
        </div>
      </Section>
      <Section title="Goals & Support Needs">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Career Goal" value={g.career_goal} /><Field label="Desired Start" value={g.desired_start_date} />
          <Field label="Financial Assistance" value={g.financial_assistance} /><Field label="Financing Options" value={g.financing_options} />
          <Field label="Barriers" value={g.barriers} /><Field label="Mentorship" value={g.interested_mentorship} />
        </div>
      </Section>
      <Section title="Preliminary Outcomes">
        <div className="space-y-2 text-sm">
          {d.cdl_result && <p><strong className="text-navy">CDL:</strong> {d.cdl_result.status} ({d.cdl_result.score}/100)</p>}
          {d.dispatcher_result && <p><strong className="text-navy">Dispatcher:</strong> {d.dispatcher_result.status}</p>}
          {d.wgu_result && <p><strong className="text-navy">WGU:</strong> {d.wgu_result.status}</p>}
          {!d.cdl_result && !d.dispatcher_result && !d.wgu_result && <p className="text-dark-text/50">No scored pathways.</p>}
        </div>
      </Section>
      <Section title="Assignments">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Enrollment Specialist" value={a.enrollment_specialist} />
          <Field label="Program Advisor" value={a.program_advisor} />
          <Field label="Dispatcher Trainer" value={a.dispatcher_trainer} />
          <Field label="Workforce Partner" value={a.workforce_partner} />
          <Field label="Mentor" value={a.mentor} />
        </div>
        <p className="text-xs text-dark-text/40 mt-4">Submitted {new Date(d.created_at).toLocaleString()} · Updated {new Date(d.updated_at).toLocaleString()}</p>
      </Section>
    </div>
  );
}

function IntakeTab({ d, g }) {
  return (
    <div className="space-y-5">
      <Section title="General Applicant Information"><div className="grid grid-cols-2 md:grid-cols-3 gap-4">{renderObj(g)}</div></Section>
      {d.cdl && <Section title="CDL Pre-Qualification Responses"><div className="grid grid-cols-2 md:grid-cols-3 gap-4">{renderObj(d.cdl)}</div></Section>}
      {d.dispatcher && <Section title="Dispatcher Responses"><div className="grid grid-cols-2 md:grid-cols-3 gap-4">{renderObj(d.dispatcher)}</div></Section>}
      {d.wgu && <Section title="WGU Responses"><div className="grid grid-cols-2 md:grid-cols-3 gap-4">{renderObj(d.wgu)}</div></Section>}
    </div>
  );
}

function Sel({ value, onChange, options, testid }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full border border-navy/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-magenta outline-none">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function QualificationTab({ d, reload }) {
  const [q, setQ] = useState(d.qualification);
  const [p, setP] = useState(d.progress_status);

  const saveStatus = async () => {
    try { await api.patch(`/admin/applicants/${d.applicant_id}/status`, { qualification: q, progress_status: p }); toast.success("Status updated"); reload(); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Section title="Qualification & Progress">
        <div className="space-y-4">
          <div><p className="text-xs text-dark-text/50 mb-1">Qualification</p><Sel value={q} onChange={setQ} options={QUALIFICATION_OPTIONS} testid="qualification-select" /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Progress Status</p><Sel value={p} onChange={setP} options={PROGRESS_OPTIONS} testid="progress-select" /></div>
          <button onClick={saveStatus} data-testid="save-status" className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors"><Save size={16} /> Save Status</button>
        </div>
        {d.cdl_result?.hard_stops?.length > 0 && (
          <div className="mt-5 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
            <strong>Hard-stop flags:</strong> {d.cdl_result.hard_stops.join("; ")}
          </div>
        )}
      </Section>
      <Section title="Assign Team">
        <TeamAssign d={d} reload={reload} />
      </Section>
    </div>
  );
}

function InterviewTab({ d, reload }) {
  const [i, setI] = useState(d.interview || {});
  const fd0 = d.final_decision || {};
  const [fd, setFd] = useState({
    pathways: fd0.pathways || [], decision_date: fd0.decision_date || "", qualification_status: fd0.qualification_status || "",
    funding_status: fd0.funding_status || "", referral_notes: fd0.referral_notes || "", next_action: fd0.next_action || "",
    follow_up_date: fd0.follow_up_date || "",
  });
  const inputCls = "w-full border border-navy/15 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-magenta outline-none";
  const set = (k, v) => setI((prev) => ({ ...prev, [k]: v }));
  const save = async () => {
    try { await api.patch(`/admin/applicants/${d.applicant_id}/interview`, i); toast.success("Interview saved"); reload(); }
    catch { toast.error("Save failed"); }
  };
  const TERMINAL = ["Not Ready — Follow-Up Required", "Hold for Future Consideration", "Not Eligible at This Time"];
  const togglePathway = (p) => setFd((prev) => {
    const has = prev.pathways.includes(p);
    if (has) return { ...prev, pathways: prev.pathways.filter((x) => x !== p) };
    // terminal outcomes are mutually exclusive with referral pathways
    if (TERMINAL.includes(p)) return { ...prev, pathways: [p] };
    return { ...prev, pathways: [...prev.pathways.filter((x) => !TERMINAL.includes(x)), p] };
  });
  const saveDecision = async () => {
    if (fd.pathways.length === 0) return toast.error("Select at least one final pathway");
    try { await api.patch(`/admin/applicants/${d.applicant_id}/final-decision`, fd); toast.success("Final decision saved — referral tools updated"); reload(); }
    catch { toast.error("Save failed"); }
  };

  const FINAL_PATHWAYS = ["160 Driving Academy — CDL-A", "AAWTA Certified Dispatcher Program", "WGU Certificate Program",
    "Mentorship or Career Pathway Support", "Not Ready — Follow-Up Required", "Hold for Future Consideration", "Not Eligible at This Time"];

  return (
    <div className="space-y-5">
      <Section title="Interview Management">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><p className="text-xs text-dark-text/50 mb-1">Has an interview taken place?</p>
            <select value={i.took_place || ""} onChange={(e) => set("took_place", e.target.value)} data-testid="interview-took_place" className={inputCls}><option value="">Select…</option><option>Yes</option><option>No</option></select></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Interview date</p>
            <input type="date" value={i.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="interview-date" className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Interview time</p>
            <input type="time" value={i.time || ""} onChange={(e) => set("time", e.target.value)} data-testid="interview-time" className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Interview method</p>
            <select value={i.method || ""} onChange={(e) => set("method", e.target.value)} data-testid="interview-method" className={inputCls}><option value="">Select…</option><option>Phone</option><option>Video</option><option>In person</option></select></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Interviewer</p>
            <input value={i.interviewer || ""} onChange={(e) => set("interviewer", e.target.value)} data-testid="interview-interviewer" className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Interview outcome</p>
            <select value={i.outcome || ""} onChange={(e) => set("outcome", e.target.value)} data-testid="interview-outcome" className={inputCls}><option value="">Select…</option><option>Positive</option><option>Needs follow-up</option><option>Not moving forward</option></select></div>
          <div className="sm:col-span-2"><p className="text-xs text-dark-text/50 mb-1">Interview notes</p>
            <textarea rows={5} value={i.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="interview-notes" placeholder="Type interview notes here. Line breaks and paragraphs are preserved." className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Required follow-up</p>
            <input value={i.follow_up_required || ""} onChange={(e) => set("follow_up_required", e.target.value)} data-testid="interview-follow_up_required" className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Next follow-up date</p>
            <input type="date" value={i.next_follow_up_date || ""} onChange={(e) => set("next_follow_up_date", e.target.value)} data-testid="interview-next_follow_up_date" className={inputCls} /></div>
        </div>
        <button onClick={save} data-testid="save-interview" className="mt-4 inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors"><Save size={16} /> Save Interview</button>
      </Section>

      <Section title="Final Program Selection & Referral Decision">
        <p className="text-xs text-dark-text/60 mb-3">Record the final approved pathway(s) after the interview. Only these activate the referral tools. Initial intake selections are interests only.</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {FINAL_PATHWAYS.map((p) => (
            <button type="button" key={p} onClick={() => togglePathway(p)} data-testid={`final-pathway-${p}`}
              className={`text-left flex items-center gap-2 px-3 py-2.5 rounded-md border text-sm transition-colors ${fd.pathways.includes(p) ? "border-magenta bg-magenta-light text-navy font-semibold" : "border-navy/15 hover:border-navy/30"}`}>
              <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${fd.pathways.includes(p) ? "bg-magenta border-magenta" : "border-navy/20"}`}>{fd.pathways.includes(p) && <Check size={11} className="text-white" />}</span>
              {p}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><p className="text-xs text-dark-text/50 mb-1">Final decision date</p>
            <input type="date" value={fd.decision_date} onChange={(e) => setFd({ ...fd, decision_date: e.target.value })} data-testid="final-decision-date" className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Qualification status</p>
            <input value={fd.qualification_status} onChange={(e) => setFd({ ...fd, qualification_status: e.target.value })} data-testid="final-qualification" className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Funding or financing status</p>
            <input value={fd.funding_status} onChange={(e) => setFd({ ...fd, funding_status: e.target.value })} data-testid="final-funding" className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Next action</p>
            <input value={fd.next_action} onChange={(e) => setFd({ ...fd, next_action: e.target.value })} data-testid="final-next-action" className={inputCls} /></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Follow-up date</p>
            <input type="date" value={fd.follow_up_date} onChange={(e) => setFd({ ...fd, follow_up_date: e.target.value })} data-testid="final-followup" className={inputCls} /></div>
          <div className="sm:col-span-2"><p className="text-xs text-dark-text/50 mb-1">Referral notes</p>
            <textarea rows={3} value={fd.referral_notes} onChange={(e) => setFd({ ...fd, referral_notes: e.target.value })} data-testid="final-referral-notes" className={inputCls} /></div>
        </div>
        {fd0.decision_by && <p className="text-xs text-dark-text/50 mt-2">Last decision by {fd0.decision_by} on {fd0.decision_date ? new Date(fd0.decision_date).toLocaleDateString() : ""}</p>}
        <button onClick={saveDecision} data-testid="save-final-decision" className="mt-4 inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors"><Save size={16} /> Save Final Decision</button>
        <p className="text-xs text-dark-text/50 mt-2">Saving updates the Referrals tab but does not send any referral until you review and approve it there.</p>
      </Section>
    </div>
  );
}

function NotesTab({ d, reload }) {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("General");
  const [follow, setFollow] = useState("");
  const add = async () => {
    if (!text.trim()) return toast.error("Note text is required");
    try { await api.post(`/admin/applicants/${d.applicant_id}/notes`, { text, category, follow_up_date: follow || null }); toast.success("Note added"); setText(""); setFollow(""); reload(); }
    catch { toast.error("Failed to add note"); }
  };
  return (
    <div className="space-y-5">
      <Section title="Add Note">
        <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a note…" data-testid="note-text"
          className="w-full border border-navy/15 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-magenta outline-none" />
        <div className="flex flex-wrap gap-3 mt-3 items-end">
          <div><p className="text-xs text-dark-text/50 mb-1">Category</p>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="note-category"
              className="border border-navy/15 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-magenta">
              {["General", "Contact", "Documents", "Funding", "Interview", "Referral"].map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div><p className="text-xs text-dark-text/50 mb-1">Follow-up date</p>
            <input type="date" value={follow} onChange={(e) => setFollow(e.target.value)} data-testid="note-followup"
              className="border border-navy/15 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-magenta" /></div>
          <button onClick={add} data-testid="add-note" className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors"><Plus size={16} /> Add Note</button>
        </div>
      </Section>
      <Section title={`Notes (${(d.notes || []).length})`}>
        {(d.notes || []).length === 0 ? <p className="text-dark-text/50 text-sm">No notes yet.</p> : (
          <div className="space-y-3">
            {[...(d.notes || [])].reverse().map((n) => (
              <div key={n.id} className={`pl-4 py-2 rounded-r-md border-l-2 ${n.system_recorded ? "border-navy bg-navy-light/50" : "border-magenta"}`} data-testid={`note-${n.id}`}>
                {n.system_recorded && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-navy bg-white border border-navy/20 rounded-full px-2 py-0.5 mb-1">
                    <Lock size={10} /> {n.category}
                  </span>
                )}
                <p className="text-sm text-dark-text">{n.text}</p>
                {n.system_recorded && n.details && (
                  <div className="mt-1.5 text-xs text-dark-text/75 grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
                    {n.details.type && <span><b className="text-navy">Type:</b> {n.details.type}</span>}
                    {n.details.partner && <span><b className="text-navy">Partner/Program:</b> {n.details.partner}</span>}
                    <span><b className="text-navy">From:</b> {n.details.sender}</span>
                    <span><b className="text-navy">To:</b> {n.details.recipient}</span>
                    {n.details.subject && <span className="sm:col-span-2"><b className="text-navy">Subject:</b> {n.details.subject}</span>}
                    {n.details.delivery_status && <span><b className="text-navy">Delivery:</b> {n.details.delivery_status}</span>}
                    {n.details.reference && <span><b className="text-navy">Ref:</b> {n.details.reference}</span>}
                    {n.details.documents?.length > 0 && <span className="sm:col-span-2"><b className="text-navy">Documents:</b> {n.details.documents.join(", ")}</span>}
                  </div>
                )}
                {n.system_recorded && n.details?.message && (
                  <p className="mt-1.5 text-xs text-dark-text/60 whitespace-pre-wrap border-t border-navy/10 pt-1.5">{n.details.message}</p>
                )}
                <p className="text-xs text-dark-text/50 mt-1">
                  {!n.system_recorded && `${n.category} · `}{n.admin_email} · {new Date(n.created_at).toLocaleString()}
                  {n.follow_up_date && ` · Follow-up: ${n.follow_up_date}`}
                  {n.system_recorded && " · System-recorded (locked)"}
                </p>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function CommsTab({ d, reload }) {
  const [templates, setTemplates] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [tkey, setTkey] = useState("");
  const [preview, setPreview] = useState(false);

  useEffect(() => { api.get("/admin/email-templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const applyTemplate = (key) => {
    setTkey(key);
    const t = templates.find((x) => x.key === key);
    if (t) { setSubject(t.subject); setMessage(t.message); }
  };
  const send = async () => {
    if (!subject || !message) return toast.error("Subject and message are required");
    try { await api.post(`/admin/applicants/${d.applicant_id}/email`, { template_key: tkey || null, subject, message }); toast.success("Email sent"); setSubject(""); setMessage(""); setTkey(""); setPreview(false); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const resend = async (c) => {
    try { await api.post(`/admin/applicants/${d.applicant_id}/email`, { template_key: c.template_key || null, subject: c.subject, message: c.message }); toast.success("Email re-sent"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); }
  };

  return (
    <div className="space-y-5">
      <Section title="Compose Email to Applicant">
        <div className="grid gap-3">
          <div><p className="text-xs text-dark-text/50 mb-1">Use a saved template</p>
            <select value={tkey} onChange={(e) => applyTemplate(e.target.value)} data-testid="email-template"
              className="w-full border border-navy/15 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-magenta">
              <option value="">— Custom email —</option>
              {templates.map((t) => <option key={t.key} value={t.key}>{t.subject}</option>)}
            </select></div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="email-subject"
            className="border border-navy/15 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-magenta" />
          <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message" data-testid="email-message"
            className="border border-navy/15 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-magenta" />
          <div className="flex gap-3">
            <button onClick={() => setPreview(!preview)} data-testid="email-preview" className="inline-flex items-center gap-2 border-2 border-navy text-navy font-bold py-2 px-5 rounded-md hover:bg-navy-light transition-colors">{preview ? "Hide Preview" : "Preview"}</button>
            <button onClick={send} data-testid="email-send" className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-2 px-5 rounded-md transition-colors"><Send size={16} /> Send Email</button>
          </div>
          {preview && (
            <div className="bg-navy-light border border-navy/10 rounded-md p-4" data-testid="email-preview-box">
              <p className="text-sm font-bold text-navy">To: {d.general.email}</p>
              <p className="text-sm font-bold text-navy">Subject: {subject}</p>
              <p className="text-sm text-dark-text/80 mt-2 whitespace-pre-wrap">{message}</p>
            </div>
          )}
        </div>
      </Section>
      <Section title={`Communications History (${(d.communications || []).length})`}>
        {(d.communications || []).length === 0 ? <p className="text-dark-text/50 text-sm">No emails sent yet.</p> : (
          <div className="space-y-3">
            {[...(d.communications || [])].reverse().map((c) => (
              <div key={c.id} className="border border-navy/10 rounded-md p-3" data-testid={`comm-${c.id}`}>
                <div className="flex justify-between items-start">
                  <p className="text-sm font-semibold text-navy">{c.subject}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.delivery_result === "sent" ? "bg-magenta-light text-magenta" : "bg-red-50 text-red-600"}`}>{c.delivery_result}</span>
                </div>
                <p className="text-sm text-dark-text/70 mt-1 whitespace-pre-wrap">{c.message}</p>
                <p className="text-xs text-dark-text/50 mt-2">To {c.to} · by {c.sender} · {new Date(c.sent_at).toLocaleString()}</p>
                {c.delivery_result === "failed" && (
                  <div className="mt-2 flex items-center justify-between gap-2 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                    <span className="text-xs text-red-700">Delivery failed{c.error ? `: ${c.error}` : ""}</span>
                    <button onClick={() => resend(c)} data-testid={`resend-${c.id}`} className="text-xs font-bold text-navy hover:text-magenta">Resend</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function ReferralsTab({ d, reload }) {
  const [r160, setR160] = useState(null);
  const [rwgu, setRwgu] = useState(null);
  const [wguEmail, setWguEmail] = useState((d.referrals?.wgu?.wgu_email) || "");
  const [roster, setRoster] = useState([]);
  const ex = d.dispatcher_trainer_assignment || {};
  const [trainer, setTrainer] = useState({
    trainer_name: ex.trainer_name || "", trainer_email: ex.trainer_email || "", training_format: ex.training_format || "Virtual",
    proposed_class: ex.proposed_class || "", start_date: ex.start_date || "", applicant_schedule: ex.applicant_schedule || "",
    financing_status: ex.financing_status || "", mentorship_request: ex.mentorship_request || "", notes: ex.notes || "",
  });

  const PR = d.program_referrals || {};
  const approved = (key) => PR[key] && !PR[key].dismissed;
  const show160 = approved("160 Driving Academy — CDL-A");
  const showWgu = approved("WGU Certificate Program");
  const showDispatch = approved("AAWTA Certified Dispatcher Program");

  useEffect(() => { if (showDispatch) api.get("/admin/trainers").then((r) => setRoster(r.data.filter((t) => t.active))).catch(() => {}); }, [showDispatch]);

  const pickTrainer = (id) => {
    const t = roster.find((x) => x.trainer_id === id);
    if (t) setTrainer((prev) => ({ ...prev, trainer_name: t.name, trainer_email: t.email, training_format: t.training_format || "Virtual" }));
  };
  const prep160 = async () => { try { const res = await api.get(`/admin/applicants/${d.applicant_id}/referral/160`); setR160(res.data); } catch { toast.error("Failed"); } };
  const prepWgu = async () => { try { const res = await api.get(`/admin/applicants/${d.applicant_id}/referral/wgu`); setRwgu(res.data); } catch { toast.error("Failed"); } };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } };
  const prAction = (pathway, action, reason) => act(() => api.patch(`/admin/applicants/${d.applicant_id}/program-referral`, { pathway, action, reason }), "Referral updated");

  const STATUS_OPTS = ["Pending Review", "Approved", "Referral Prepared", "Referral Sent", "Accepted", "Enrolled", "On Hold", "Dismissed", "Declined"];
  const approvedKeys = Object.keys(PR);
  const notApprovedInterests = (d.programs || []).filter((p) => {
    const map = { "Class A CDL through 160 Driving Academy": "160 Driving Academy — CDL-A", "WGU Behind-the-Desk Certificate": "WGU Certificate Program", "AAWTA Certified Dispatcher Program": "AAWTA Certified Dispatcher Program", "Mentorship and Career Pathway Support": "Mentorship or Career Pathway Support" };
    return !PR[map[p]];
  });

  return (
    <div className="space-y-5">
      {/* Initial interests (not approved) */}
      {notApprovedInterests.length > 0 && (
        <Section title="Initial Interest — Not Yet Approved">
          <p className="text-xs text-dark-text/60 mb-3">These intake selections are interests only. Record the final decision under Interview Management to activate their referral tools.</p>
          <div className="flex flex-wrap gap-2" data-testid="initial-interests">
            {notApprovedInterests.map((p) => <span key={p} className="bg-soft-gray border border-navy/15 text-dark-text/70 text-xs font-semibold px-3 py-1.5 rounded-full">{p} · Not Yet Approved</span>)}
          </div>
        </Section>
      )}

      {/* Approved program referrals with status + dismiss/hold controls */}
      {approvedKeys.length > 0 && (
        <Section title="Approved Program Referrals">
          <div className="space-y-3" data-testid="approved-referrals">
            {approvedKeys.map((k) => {
              const r = PR[k];
              return (
                <div key={k} className={`border rounded-md p-3 ${r.dismissed ? "border-navy/10 bg-soft-gray opacity-80" : "border-navy/15"}`} data-testid={`pr-${k}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-navy text-sm">{k}</p>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-magenta">{r.status}{r.on_hold ? " · On Hold" : ""}{r.dismissed ? " · Dismissed" : ""}</span>
                      {r.dismiss_reason && <p className="text-xs text-dark-text/50 mt-0.5">Reason: {r.dismiss_reason}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {!r.dismissed && (
                        <select value={r.status} onChange={(e) => act(() => api.patch(`/admin/applicants/${d.applicant_id}/program-referral`, { pathway: k, action: "set_status", status: e.target.value }), "Status updated")} data-testid={`pr-status-${k}`}
                          className="border border-navy/15 rounded-md px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-magenta">
                          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      )}
                      {!r.dismissed && !r.on_hold && <button onClick={() => prAction(k, "hold")} data-testid={`pr-hold-${k}`} className="text-xs font-bold text-navy border border-navy/20 rounded px-2 py-1 hover:bg-navy-light">Place on Hold</button>}
                      {r.on_hold && <button onClick={() => prAction(k, "reactivate")} data-testid={`pr-reactivate-${k}`} className="text-xs font-bold text-navy border border-navy/20 rounded px-2 py-1 hover:bg-navy-light">Reactivate</button>}
                      {!r.dismissed ? (
                        <button onClick={() => { const reason = window.prompt("Dismissal reason?"); if (reason) prAction(k, "dismiss", reason); }} data-testid={`pr-dismiss-${k}`} className="text-xs font-bold text-red-600 border border-red-200 rounded px-2 py-1 hover:bg-red-50">Dismiss</button>
                      ) : (
                        <button onClick={() => prAction(k, "restore")} data-testid={`pr-restore-${k}`} className="text-xs font-bold text-magenta border border-magenta/30 rounded px-2 py-1 hover:bg-magenta-light">Restore</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {approvedKeys.length === 0 && (
        <Section title="Referrals">
          <p className="text-dark-text/50 text-sm" data-testid="referrals-empty">No approved pathways yet. Record the Final Program Selection under Interview Management to activate referral tools.</p>
        </Section>
      )}
      {show160 && (
        <Section title="160 Driving Academy Referral">
          <div className="flex flex-wrap gap-3 mb-4">
            <button onClick={prep160} data-testid="prep-160" className="inline-flex items-center gap-2 border-2 border-navy text-navy font-bold py-2 px-4 rounded-md hover:bg-navy-light transition-colors"><Truck size={16} /> Prepare 160 Referral</button>
            <button onClick={() => act(() => api.post(`/admin/applicants/${d.applicant_id}/referral/160/email`), "Referral email sent")} data-testid="email-160" className="inline-flex items-center gap-2 bg-navy text-white font-bold py-2 px-4 rounded-md hover:bg-navy-dark transition-colors"><Mail size={16} /> Email 160 Referral</button>
            <a href="https://www.160drivingacademy.com/?utm_source=Internal_Marketing&utm_medium=AAWTA" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-magenta text-magenta font-bold py-2 px-4 rounded-md hover:bg-magenta-light transition-colors">Open AAWTA 160 Page</a>
            <button onClick={() => act(() => api.post(`/admin/applicants/${d.applicant_id}/referral/160/mark-referred`), "Marked as referred")} data-testid="mark-160-referred" className="inline-flex items-center gap-2 bg-magenta text-white font-bold py-2 px-4 rounded-md hover:bg-magenta-dark transition-colors">Mark as Referred</button>
          </div>
          <p className="text-xs text-dark-text/50 mb-3">A referral is not transmitted until an AAWTA administrator approves the applicant.</p>
          {r160 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-navy-light rounded-md p-4" data-testid="referral-160-data">
              {renderObj({
                reference_number: r160.reference_number, first_name: r160.first_name, last_name: r160.last_name,
                email: r160.email, phone: r160.phone, address: r160.address,
                date_of_birth: r160.date_of_birth, age: r160.age,
                active_license: r160.active_license,
                license_suspended: r160.driving_history?.suspended,
                tickets_citations: r160.driving_history?.tickets,
                serious_violation: r160.driving_history?.serious_violation,
                dot_physical: r160.dot_physical, drug_screen: r160.drug_screen,
                work_authorization: r160.work_authorization, funding_need: r160.funding_need,
                preferred_location: r160.preferred_location, preliminary_score: r160.preliminary_score,
                qualification_outcome: r160.qualification_outcome, aawta_approval_status: r160.aawta_approval_status,
                aawta_advisor: r160.aawta_advisor,
                referral_date: r160.referral_date || "Not referred yet",
              })}
            </div>
          )}
        </Section>
      )}

      {showWgu && (
        <Section title="WGU Referral">
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <button onClick={prepWgu} data-testid="prep-wgu" className="inline-flex items-center gap-2 border-2 border-navy text-navy font-bold py-2 px-4 rounded-md hover:bg-navy-light transition-colors"><GraduationCap size={16} /> Prepare / Preview Referral</button>
            <input value={wguEmail} onChange={(e) => setWguEmail(e.target.value)} placeholder="Approved WGU partner email" data-testid="wgu-email"
              className="border border-navy/15 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-magenta min-w-[240px]" />
            <button onClick={() => { if (!wguEmail) return toast.error("Enter the WGU partner email"); act(() => api.post(`/admin/applicants/${d.applicant_id}/referral/wgu/email`, { wgu_email: wguEmail }), "WGU referral sent"); }} data-testid="email-wgu" className="inline-flex items-center gap-2 bg-navy text-white font-bold py-2 px-4 rounded-md hover:bg-navy-dark transition-colors"><Mail size={16} /> Email WGU</button>
            <button onClick={() => act(() => api.post(`/admin/applicants/${d.applicant_id}/referral/wgu/mark-referred`), "Marked as referred")} data-testid="mark-wgu-referred" className="inline-flex items-center gap-2 bg-magenta text-white font-bold py-2 px-4 rounded-md hover:bg-magenta-dark transition-colors">Mark as Referred</button>
          </div>
          <p className="text-xs text-dark-text/50 mb-3">Enter the approved WGU partner email address — it is never guessed.</p>
          {rwgu && <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-navy-light rounded-md p-4" data-testid="referral-wgu-data">{renderObj(rwgu)}</div>}
        </Section>
      )}

      {showDispatch && (
        <Section title="Dispatcher Trainer Assignment">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-navy">Assign from roster:</span>
            {roster.length === 0 ? (
              <span className="text-xs text-dark-text/50">No saved trainers yet — add them on the Trainers page, or enter details below.</span>
            ) : (
              <select onChange={(e) => pickTrainer(e.target.value)} data-testid="trainer-roster-picker" defaultValue=""
                className="border border-navy/15 rounded-md px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-magenta">
                <option value="">Choose a certified trainer…</option>
                {roster.map((t) => <option key={t.trainer_id} value={t.trainer_id}>{t.name} · {t.current_availability} · load {t.current_load}/{t.max_capacity ?? "∞"} · next {t.next_available_date || "—"} · {(t.formats?.length ? t.formats.join("/") : t.training_format)}{t.specialty ? ` · ${t.specialty}` : ""}{t.fully_booked ? " · FULL" : ""}</option>)}
              </select>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[["trainer_name", "Trainer name"], ["trainer_email", "Trainer email"], ["proposed_class", "Proposed class"], ["applicant_schedule", "Applicant schedule"], ["financing_status", "Financing status"], ["mentorship_request", "Mentorship request"]].map(([k, l]) => (
              <div key={k}><p className="text-xs text-dark-text/50 mb-1">{l}</p>
                <input value={trainer[k]} onChange={(e) => setTrainer({ ...trainer, [k]: e.target.value })} data-testid={`trainer-${k}`}
                  className="w-full border border-navy/15 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-magenta" /></div>
            ))}
            <div><p className="text-xs text-dark-text/50 mb-1">Training format</p>
              <select value={trainer.training_format} onChange={(e) => setTrainer({ ...trainer, training_format: e.target.value })} data-testid="trainer-format"
                className="w-full border border-navy/15 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-magenta"><option>Virtual</option><option>In person</option></select></div>
            <div><p className="text-xs text-dark-text/50 mb-1">Start date</p>
              <input type="date" value={trainer.start_date} onChange={(e) => setTrainer({ ...trainer, start_date: e.target.value })} data-testid="trainer-start"
                className="w-full border border-navy/15 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-magenta" /></div>
            <div><p className="text-xs text-dark-text/50 mb-1">Session date / start / end (for double-booking check)</p>
              <div className="flex gap-2">
                <input type="date" value={trainer.session_date || ""} onChange={(e) => setTrainer({ ...trainer, session_date: e.target.value })} data-testid="trainer-session-date" className="flex-1 min-w-0 border border-navy/15 rounded-md px-2 py-2 text-sm" />
                <input type="time" value={trainer.session_start || ""} onChange={(e) => setTrainer({ ...trainer, session_start: e.target.value })} data-testid="trainer-session-start" className="w-28 border border-navy/15 rounded-md px-2 py-2 text-sm" />
                <input type="time" value={trainer.session_end || ""} onChange={(e) => setTrainer({ ...trainer, session_end: e.target.value })} data-testid="trainer-session-end" className="w-28 border border-navy/15 rounded-md px-2 py-2 text-sm" />
              </div></div>
            <div className="sm:col-span-2"><p className="text-xs text-dark-text/50 mb-1">Assignment notes</p>
              <textarea rows={2} value={trainer.notes} onChange={(e) => setTrainer({ ...trainer, notes: e.target.value })} data-testid="trainer-notes"
                className="w-full border border-navy/15 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-magenta" /></div>
          </div>
          <button onClick={async () => {
            if (!trainer.trainer_name || !trainer.trainer_email) return toast.error("Trainer name and email required");
            try { await api.post(`/admin/applicants/${d.applicant_id}/dispatcher-trainer`, trainer); toast.success("Trainer assigned & notifications sent"); reload(); }
            catch (e) {
              const det = e.response?.data?.detail;
              if (det?.code === "trainer_warning") {
                if (window.confirm(`Trainer warning:\n\n${det.warnings.join("\n")}\n\nOverride and assign anyway?`)) act(() => api.post(`/admin/applicants/${d.applicant_id}/dispatcher-trainer`, { ...trainer, override: true }), "Trainer assigned (warning overridden)");
              } else toast.error(typeof det === "string" ? det : "Action failed");
            }
          }}
            data-testid="assign-trainer" className="mt-4 inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors"><Headset size={16} /> Assign Certified Trainer</button>
          {ex.assigned_at && (
            <div className="mt-4 bg-navy-light border border-navy/10 rounded-md p-4" data-testid="saved-trainer-assignment">
              <p className="text-xs font-bold text-navy mb-2">Saved Assignment Record</p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-dark-text/75">
                <span><b className="text-navy">Trainer:</b> {ex.trainer_name} ({ex.trainer_email})</span>
                <span><b className="text-navy">Format:</b> {ex.training_format || "—"}</span>
                <span><b className="text-navy">Proposed class:</b> {ex.proposed_class || "—"}</span>
                <span><b className="text-navy">Start date:</b> {ex.start_date || "—"}</span>
                <span><b className="text-navy">Applicant availability:</b> {ex.applicant_schedule || "—"}</span>
                <span><b className="text-navy">Financing:</b> {ex.financing_status || "—"}</span>
                <span><b className="text-navy">Mentorship:</b> {ex.mentorship_request || "—"}</span>
                <span><b className="text-navy">Assigned by:</b> {ex.assigned_by}</span>
                <span><b className="text-navy">Date assigned:</b> {new Date(ex.assigned_at).toLocaleString()}</span>
                <span><b className="text-navy">Confirmation:</b> {ex.confirmation_status || "—"}{ex.confirmation_sent_at ? ` (${new Date(ex.confirmation_sent_at).toLocaleString()})` : ""}</span>
                {ex.notes && <span className="sm:col-span-2"><b className="text-navy">Notes:</b> {ex.notes}</span>}
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

function DocsTab({ d, reload }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/applicants/${d.applicant_id}/documents`, fd); toast.success("Document uploaded"); reload(); }
    catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const download = async (doc) => {
    try { const res = await api.get(`/admin/documents/${doc.id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = doc.original_filename; a.click(); URL.revokeObjectURL(url); }
    catch { toast.error("Download failed"); }
  };
  const docs = (d.documents || []).filter((x) => !x.is_deleted);
  return (
    <Section title="Secure Documents">
      <label className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors cursor-pointer w-fit" data-testid="upload-doc-label">
        <Upload size={16} /> {uploading ? "Uploading…" : "Upload Document"}
        <input type="file" className="hidden" onChange={upload} data-testid="upload-doc-input" disabled={uploading} />
      </label>
      <p className="text-xs text-dark-text/50 mt-2 mb-4">Documents are stored securely and are not publicly accessible.</p>
      {docs.length === 0 ? <p className="text-dark-text/50 text-sm">No documents uploaded.</p> : (
        <div className="divide-y divide-navy/5">
          {docs.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between py-3" data-testid={`doc-${doc.id}`}>
              <div><p className="text-sm font-semibold text-navy">{doc.original_filename}</p><p className="text-xs text-dark-text/50">{(doc.size / 1024).toFixed(1)} KB · {doc.uploaded_by} · {new Date(doc.created_at).toLocaleString()}</p></div>
              <button onClick={() => download(doc)} className="inline-flex items-center gap-1 text-navy hover:text-magenta text-sm font-semibold"><Download size={16} /> Download</button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function ActivityTab({ d }) {
  const items = [...(d.activity || [])].reverse();
  return (
    <Section title="Activity History">
      {items.length === 0 ? <p className="text-dark-text/50 text-sm">No activity recorded.</p> : (
        <div className="space-y-4">
          {items.map((e) => (
            <div key={e.id} className="flex gap-3" data-testid={`activity-${e.id}`}>
              <div className="w-2.5 h-2.5 rounded-full bg-magenta mt-1.5 shrink-0" />
              <div>
                <p className="text-sm text-navy font-semibold">{e.action}</p>
                <p className="text-sm text-dark-text/70">{e.detail}</p>
                <p className="text-xs text-dark-text/40 mt-0.5">{e.actor} · {new Date(e.timestamp).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
